import dayjs from "dayjs";

import type { ApiResponse } from "../../../../services/apiService";
import {
  PayerTier,
  ActionType,
  ClaimAppeal,
  ClaimPayer,
  ClaimAppealLetter,
  ClaimStatus,
  FeeBaselineFallback,
} from "../../../../types/claims";

// Contract: app-backend docs/claims/claims-manual-edits.md, "Record an action" (REV-2538/2542/2593/2628)

export const PAYER_TIER_LABELS: Record<PayerTier, string> = {
  primary: "Primary payer",
  secondary: "Secondary payer",
  tertiary: "Tertiary payer",
};

// Why the fee keeps the stored "paid at appeal" snapshot instead of rebuilding it from the remits
export const BASELINE_FALLBACK_MESSAGES: Record<FeeBaselineFallback, string> = {
  payment_without_date:
    "The paid amount at the appeal date is estimated: a remit has no date.",
  paid_amount_mismatch:
    "The paid amount at the appeal date is estimated: the remits don't add up to the claim's paid amount.",
};

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  appeal: "Appeal",
  dofr: "DOFR Appeal",
  correct_resubmit: "Correct and resubmit",
};

export const CORRECT_RESUBMIT: ActionType = "correct_resubmit";

/** An appeal or DOFR appeal, which marks the claim Appealed; a correct and resubmit doesn't. */
export const isAppealAction = (type: ActionType | "" | null | undefined) =>
  type === "appeal" || type === "dofr";

export const hasCorrectResubmit = (appeals?: ClaimAppeal[] | null) =>
  !!appeals?.some((appeal) => appeal.actionType === CORRECT_RESUBMIT);

/** The timeline title of a recorded action. */
export const actionTitle = (appeal: ClaimAppeal) =>
  appeal.actionType === CORRECT_RESUBMIT ? "Corrected and resubmitted" : "Appealed";

/** "Jun 3, 2026 · Primary payer"; a correct and resubmit has no payer. */
export const actionCaption = (
  appeal: ClaimAppeal,
  formatDate: (value: string) => string,
) => {
  const tier = appeal.payerTier
    ? (PAYER_TIER_LABELS[appeal.payerTier] ?? appeal.payerTier)
    : "";
  return [formatDate(appeal.actionOn), tier].filter(Boolean).join(" · ");
};

// Mirrors the backend's APPEAL_TYPE_BY_LETTER: with a letter, its type wins over an appeal / DOFR input
const APPEAL_TYPE_BY_LETTER: Record<string, ActionType> = {
  appeal_letter: "appeal",
  dofr_appeal_letter: "dofr",
};

export const MAX_CLAIM_DOCUMENT_MB = 10;
const MAX_CLAIM_DOCUMENT_BYTES = MAX_CLAIM_DOCUMENT_MB * 1024 * 1024;
const PDF_CONTENT_TYPES = ["application/pdf", "application/x-pdf"];

/** The payer the claim's status points at: a secondary/tertiary denial, else the primary. */
export const payerTierFromStatus = (
  status?: ClaimStatus | string | null,
): PayerTier => {
  if (status === ClaimStatus.SECONDARY_DENIED) return "secondary";
  if (status === ClaimStatus.TERTIARY_DENIED) return "tertiary";
  return "primary";
};

const PAYER_LEVEL_LABELS: Record<PayerTier, string> = {
  primary: "Primary",
  secondary: "Secondary",
  tertiary: "Tertiary",
};

const ALL_PAYERS: ClaimPayer[] = (Object.keys(PAYER_LEVEL_LABELS) as PayerTier[]).map(
  (tier) => ({ tier, name: null }),
);

/** The claim's payer levels; every level until the claim detail has loaded them. */
const payerLevels = (payers?: ClaimPayer[] | null): ClaimPayer[] =>
  payers?.length ? payers : ALL_PAYERS;

export const payerName = (
  tier: PayerTier,
  payers?: ClaimPayer[] | null,
): string => payers?.find((payer) => payer.tier === tier)?.name ?? "";

/** "Primary - Aetna", or just "Primary" when the name isn't known. */
export const payerLabel = (
  tier: PayerTier,
  payers?: ClaimPayer[] | null,
): string => {
  const name = payerName(tier, payers);
  return name ? `${PAYER_LEVEL_LABELS[tier]} - ${name}` : PAYER_LEVEL_LABELS[tier];
};

/** Payer picker options: only the levels the claim has (REV-2621). */
export const payerOptions = (payers?: ClaimPayer[] | null) =>
  payerLevels(payers).map(({ tier }) => ({
    value: tier,
    label: payerLabel(tier, payers),
  }));

/** The status's payer when the claim has that level, else its first (the primary). */
export const defaultPayerTier = (
  status: ClaimStatus | string | null | undefined,
  payers?: ClaimPayer[] | null,
): PayerTier => {
  const levels = payerLevels(payers);
  const tier = payerTierFromStatus(status);
  return levels.some((payer) => payer.tier === tier) ? tier : levels[0].tier;
};

/** The appeal type the claim's first letter fixes (see getFirstAppealLetter), or null. */
export const appealTypeFromLetter = (
  letter: ClaimAppealLetter | null,
): ActionType | null => (letter && APPEAL_TYPE_BY_LETTER[letter.type]) || null;

/** A date as YYYY-MM-DD in the browser's timezone, which is the one sent with the appeal. */
export const toLocalIsoDate = (
  value: string | Date = new Date(),
): string | null => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

// The API caps an appeal date at today in this timezone (app-backend #677)
const SERVER_TIMEZONE = "America/New_York";

/** Today as YYYY-MM-DD in `timeZone`, or null if the browser can't resolve it. */
const isoDateIn = (timeZone: string, now: Date): string | null => {
  try {
    // en-CA formats as YYYY-MM-DD
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return null;
  }
};

/** The latest appeal date the API accepts: the earlier of today in the browser's
 * timezone and today in US/Eastern. East of New York, local "today" starts hours
 * before the server's, and the default date would be refused. */
export const latestAppealDate = (now: Date = new Date()): string => {
  const local = toLocalIsoDate(now) as string;
  const server = isoDateIn(SERVER_TIMEZONE, now);
  return server && server < local ? server : local;
};

export const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

/** Why `file` can't be a claim document (an appeal's or a payment's correspondence), or null. The server also checks the %PDF header. */
export const claimDocumentError = (file: File): string | null => {
  const isPdfName = file.name.toLowerCase().endsWith(".pdf");
  // Some Windows setups report no type for a PDF; the name then has to do
  const type = (file.type || "").toLowerCase();
  const isPdfType = !type || PDF_CONTENT_TYPES.includes(type);
  if (!isPdfName || !isPdfType)
    return `"${file.name}" isn't a PDF. Only PDF files are accepted.`;
  if (file.size > MAX_CLAIM_DOCUMENT_BYTES) {
    return `"${file.name}" is larger than ${MAX_CLAIM_DOCUMENT_MB} MB.`;
  }
  return null;
};

export type AppealFormField =
  | "actionOn"
  | "payerTier"
  | "actionType"
  | "comment"
  | "document";
export type AppealFieldErrors = Partial<Record<AppealFormField, string>>;

/** The same rules the API applies, checked before asking it for the fee preview. */
export const validateAppealForm = (form: {
  actionOn: string;
  /** Empty when not chosen. */
  actionType: ActionType | "";
  today: string;
  /** The first letter's date; a correct and resubmit isn't held to it. */
  minDate: string | null;
}): AppealFieldErrors => {
  const errors: AppealFieldErrors = {};
  if (!form.actionType) {
    errors.actionType = "Choose the action.";
  }
  const minDate = form.actionType === CORRECT_RESUBMIT ? null : form.minDate;
  if (!form.actionOn) {
    errors.actionOn = "Enter the date of the action.";
  } else if (form.actionOn > form.today) {
    errors.actionOn = "Can't be in the future.";
  } else if (minDate && form.actionOn < minDate) {
    errors.actionOn = `Can't be before the first appeal letter (${dayjs(minDate).format("MMM D, YYYY")}).`;
  }
  return errors;
};

export type AppealErrorCode =
  | "validation_error"
  | "claim_invoiced"
  | "duplicate_appeal"
  | "already_resubmitted"
  | "unknown";

/** A claim edit (appeal, manual payment) the API refused, as a dialog can show it. */
export interface ClaimEditError<Code extends string, Field extends string> {
  code: Code | "unknown";
  message: string;
  fieldErrors: Partial<Record<Field, string>>;
}

export type AppealError = ClaimEditError<AppealErrorCode, AppealFormField>;

/** The API's error envelope ({error, message, details}); `details` maps a field to
 * its messages on a validation_error. Codes and fields outside the lists are dropped. */
export const parseClaimEditError = <Code extends string, Field extends string>(
  response: ApiResponse<unknown>,
  knownCodes: readonly Code[],
  fields: readonly Field[],
): ClaimEditError<Code, Field> => {
  const body = (
    response.data && typeof response.data === "object" ? response.data : {}
  ) as {
    error?: unknown;
    details?: unknown;
  };
  const code = knownCodes.find((c) => c === body.error) ?? "unknown";

  const fieldErrors: Partial<Record<Field, string>> = {};
  if (
    body.error === "validation_error" &&
    body.details &&
    typeof body.details === "object"
  ) {
    const details = body.details as Record<string, unknown>;
    for (const field of fields) {
      const messages = details[field];
      if (Array.isArray(messages) && messages.length)
        fieldErrors[field] = messages.join(" ");
    }
  }

  return {
    code,
    message: response.error || "Something went wrong. Please try again.",
    fieldErrors,
  };
};

const KNOWN_CODES: AppealErrorCode[] = [
  "validation_error",
  "claim_invoiced",
  "duplicate_appeal",
  "already_resubmitted",
];

const FIELDS: AppealFormField[] = [
  "actionOn",
  "payerTier",
  "actionType",
  "comment",
  "document",
];

export const parseAppealError = (response: ApiResponse<unknown>): AppealError =>
  parseClaimEditError(response, KNOWN_CODES, FIELDS);

/** Errors after which saving can't succeed until something on the claim changes. */
export const isBlockingAppealError = (code: AppealErrorCode) =>
  code === "claim_invoiced" ||
  code === "duplicate_appeal" ||
  code === "already_resubmitted";

export const formatFeeAmount = (value: string | null | undefined): string => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (isNaN(amount)) return value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const formatFeePct = (value: string | null | undefined): string => {
  if (value == null || value === "") return "—";
  const pct = Number(value);
  return isNaN(pct) ? value : `${pct}%`;
};

/** "2026-07" -> "Jul 2026" */
export const formatFeeMonth = (month: string): string => {
  const parsed = dayjs(`${month}-01`);
  return parsed.isValid() ? parsed.format("MMM YYYY") : month;
};
